import * as Sentry from '@sentry/react';
import { reportInteractConversion, reportImpressionConversion } from './conversions.ts';
import { createVisibleDwellClock } from './dwell-clock.ts';
import { pathSegmentName } from './page-name.ts';

type Attributes = Record<string, string | number | boolean>;

// Every custom span op is namespaced under `gp.` so it can't collide with a
// Sentry built-in op. This bit us before: `ui.interaction.click` is *also* the
// op Sentry's INP (Interaction to Next Paint) web-vital spans use, so a
// `span.op:ui.interaction.click` query silently mixed our CTA-click spans in
// with Sentry's INP spans (which carry no `path`/`target`, so they showed up as
// a "(no value)" target bucket). Keep new ops `gp.`-prefixed and static — op
// values live in historical telemetry and dashboards group by them, so a
// rename splits past aggregations from future ones.
const SPAN_OP = {
  click: 'gp.ui.click',
  impression: 'gp.ui.impression',
  pageview: 'gp.ui.pageview',
} as const;

// Metric names share the `gp.` namespace and the same rename caveat: a metric's
// history lives under its name.
const METRIC = {
  pageviewDwell: 'gp.ui.pageview.dwell',
} as const;

// The interaction categories we emit. Sentry groups spans by `op`, so this
// stays a small closed union — add a member to introduce a new kind.
type InteractionOp = typeof SPAN_OP.click | typeof SPAN_OP.impression;

// Records a UI interaction as a Sentry span.
//   - `op` is the interaction category. Sentry groups spans by `op`, so each
//     kind aggregates cleanly in Trace Explorer and on dashboards.
//   - the span name is the element's dotted `path` (e.g.
//     'home.hero.signup-cta'), so a span reads as *what* was interacted with.
//     Pass a `path` attribute; without one the name falls back to `op`.
// The span attaches to the active trace, drillable back to the surrounding
// session replay (when sampled) and any errors.
function trackInteraction(op: InteractionOp, attributes: Attributes = {}): void {
  const name = typeof attributes.path === 'string' ? attributes.path : op;
  Sentry.startSpan({ name, op, attributes }, () => {
    // The span itself is the signal; no work to perform inside it.
  });
}

// How a page visit ended. Observed locally at the moment of exit — stamping it
// here (rather than reconstructing it later by pairing a separate "enter" and
// "exit" event) is what keeps bounce analysis a single-dataset Sentry query:
// the query layer has no joins or sessionization to do the pairing itself.
type PageExitReason = 'pagehide' | 'spa-navigation';

// Emits exactly one `gp.ui.pageview` span per visit to a page, carrying only the
// raw facts needed to compute time-on-page and bounce rate *after the fact* in
// Sentry — never a derived verdict. No "bounce" rule and no thresholds live
// here on purpose: a bounce is whatever a dashboard query says it is, so the
// definition can change without a deploy. See docs/ui-instrumentation.md.
//
// The visible dwell time also goes out as the `gp.ui.pageview.dwell`
// distribution metric (unit: millisecond), with `page` and `exit_reason` as
// attributes. Percentiles of time on page (`p50`, `p75`, `p95`) come from the
// metric, which Sentry aggregates natively; the span keeps the per-visit
// record for bounce counts, per-visit filters, and the drill-down into the
// surrounding trace and replay.
//
// Span attributes:
//   - `page`:        the visited route, as the matched route's `fullPath`
//                    (e.g. '/'). Named `page`, not `route`, because Sentry has a
//                    reserved `route` span field that would otherwise shadow it
//                    in queries.
//   - `dwell_ms`:    ms the page was *visible* during the visit (see
//                    `createVisibleDwellClock`). Time the tab spent hidden in the
//                    background is excluded, so an abandoned tab closed hours
//                    later records only its engaged time, not the idle wait.
//                    Measured in the browser because it is the only place that
//                    sees both ends of a visit — Sentry can't subtract one
//                    event's time from another's. In a span query string,
//                    filter it in the number-typed form
//                    (`tags[dwell_ms,number]:<10000`): a bare `dwell_ms`
//                    resolves to the string copy.
//   - `exit_reason`: `pagehide` (left the document: tab close, reload, off-site
//                    nav) or `spa-navigation` (client-side route change).
//
// Call from a page component's mount effect, passing the visited route (e.g. the
// matched route's `fullPath` from `useRouterState`) and invoking the returned
// disposer in cleanup so an in-app navigation records as `spa-navigation`:
//
//   useEffect(() => trackPageView(page), [page]);
//
export function trackPageView(page: string): () => void {
  const dwell = createVisibleDwellClock(() => performance.now(), document.visibilityState === 'visible');
  let emitted = false;

  const emit = (exitReason: PageExitReason): void => {
    if (emitted) return;
    emitted = true;
    // Settle any open visible interval into the total before reading it.
    dwell.hide();
    const dwellMs = Math.round(dwell.elapsedMs());
    // `dwell_ms` rides as an attribute rather than as the span's own (trace
    // waterfall) duration: the span is created here at exit, so its duration is
    // ~0. A live span started at mount would show a real duration, but
    // browser-tracing's pageload root span idle-closes within ~30s
    // (finalTimeout), so for the long visits this measures, a mount-started
    // child span would be truncated or dropped. One self-contained span
    // emitted at exit keeps delivery reliable.
    Sentry.startSpan(
      {
        name: page,
        op: SPAN_OP.pageview,
        attributes: { page, dwell_ms: dwellMs, exit_reason: exitReason },
      },
      () => {
        // The span itself is the signal; no work to perform inside it.
      },
    );
    Sentry.metrics.distribution(METRIC.pageviewDwell, dwellMs, {
      unit: 'millisecond',
      attributes: { page, exit_reason: exitReason },
    });
  };

  // Pause the dwell clock while the tab is backgrounded so hidden time doesn't
  // count toward engaged dwell. On a real tab close, `visibilitychange` to
  // hidden fires *before* `pagehide`, so the clock is already settled by the
  // time the span emits.
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') dwell.show();
    else dwell.hide();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  // `pagehide` is the reliable "leaving the document" signal: it fires on tab
  // close, reload, and cross-document navigation (including into the bfcache),
  // where `beforeunload`/`unload` are unreliable — notably on mobile.
  const onPageHide = (): void => emit('pagehide');
  window.addEventListener('pagehide', onPageHide);

  // Cleanup runs on in-app route changes (the component unmounts) but NOT on a
  // full-document unload — the browser tears the page down without running
  // effect cleanups there, so `pagehide` covers that path. The `emitted` guard
  // makes whichever fires first win.
  return () => {
    window.removeEventListener('pagehide', onPageHide);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    emit('spa-navigation');
  };
}

// Declarative DOM instrumentation. Two sinks ride the same observers — a Sentry
// span (`observe`) and a LinkedIn ad-conversion beacon (`conversion`) — off the
// same two triggers. Elements opt in via:
//   - `data-gp-observe-interact`      — emit a `gp.ui.click` span on click
//   - `data-gp-observe-impression`    — emit a `gp.ui.impression` span when seen
//   - `data-gp-conversion-interact`   — fire a conversion beacon on click
//   - `data-gp-conversion-impression` — fire a conversion beacon when seen
// An element may carry both families (the Sign Up CTA does): one observer pass,
// two sinks. The `observe` spans carry attributes:
//   - `path`: dotted chain of `data-gp-name` ancestors (outer → inner)
//   - `target`: the observed element's own `data-gp-name`
// The same `data-gp-name` selectors are reusable from Playwright tests.

// An impression means the visitor actually saw the element. Two independent
// rules satisfy it, and an element needs only one:
//   - ELEMENT_FRACTION: at least half the *element* is on screen — the rule for
//     anything smaller than the viewport.
//   - VIEWPORT_FRACTION: the element covers at least half the *viewport* — the
//     rule for sections taller than the viewport, which can never show 50% of
//     their own area at once (a 3×-viewport hero peaks near 33% visible) and so
//     would otherwise never register an impression at all.
const IMPRESSION_ELEMENT_FRACTION = 0.5;
const IMPRESSION_VIEWPORT_FRACTION = 0.5;
const IMPRESSION_DWELL_MS = 200;

// IntersectionObserver only delivers a callback when the *element's* visible
// fraction crosses one of these thresholds. A section taller than the viewport
// can never cross a high threshold — only a viewport's worth of it is ever on
// screen at once — so the ladder steps finely from 0, guaranteeing a callback
// lands while such a section fills the screen. Each callback re-checks the
// rules above, keeping the sampling cadence independent of what counts as seen;
// the 0 rung also fires as an element scrolls fully out, letting a pending
// timer be cleared.
const IMPRESSION_THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20);

// Both impression sinks ride one IntersectionObserver, so observe the union of
// the Sentry and conversion impression selectors — a conversion-only element
// (no `data-gp-name`, no span) is still tracked.
const IMPRESSION_SELECTOR = '[data-gp-observe-impression], [data-gp-conversion-impression]';
const CONVERSION_INTERACT_ATTR = 'data-gp-conversion-interact';
const CONVERSION_IMPRESSION_ATTR = 'data-gp-conversion-impression';

let installed = false;
const impressed = new WeakSet<Element>();
const pendingImpression = new WeakMap<Element, number>();

export function installInteractionObservers(): void {
  if (installed) return;
  installed = true;

  // Capture phase so we record the click intent even if a downstream
  // handler calls stopPropagation.
  document.addEventListener('click', handleClick, true);

  const intersection = new IntersectionObserver(handleIntersection, {
    threshold: IMPRESSION_THRESHOLDS,
  });

  for (const el of document.querySelectorAll(IMPRESSION_SELECTOR)) {
    intersection.observe(el);
  }

  // Watch for elements added later (route changes, lazy mounts). Removed
  // elements are GC'd from the WeakSet/WeakMap automatically.
  const mutations = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(IMPRESSION_SELECTOR)) {
          intersection.observe(node);
        }
        for (const el of node.querySelectorAll(IMPRESSION_SELECTOR)) {
          intersection.observe(el);
        }
      }
    }
  });
  mutations.observe(document.body, { childList: true, subtree: true });
}

function handleClick(event: Event): void {
  const eventTarget = event.target;
  if (!(eventTarget instanceof Element)) return;

  const observed = eventTarget.closest('[data-gp-observe-interact]');
  if (observed instanceof HTMLElement) {
    const payload = buildPayload(observed);
    if (payload) trackInteraction(SPAN_OP.click, payload);
  } else {
    // Navigation records itself; everything else opts in above. Keyed on the
    // anchor so bare `<a>`s and router-imported links are covered too.
    const link = eventTarget.closest('a[href]');
    if (link instanceof HTMLAnchorElement) {
      const payload = buildPayload(link, linkName(link));
      if (payload) trackInteraction(SPAN_OP.click, payload);
    }
  }

  // Same click, second sink: if the element opted into a conversion, fire the
  // ad beacon. The gate (an attributable li_fat_id) lives in the sink.
  const converted = eventTarget.closest(`[${CONVERSION_INTERACT_ATTR}]`);
  if (converted instanceof HTMLElement) {
    const name = converted.getAttribute(CONVERSION_INTERACT_ATTR);
    if (name) reportInteractConversion(name);
  }
}

function handleIntersection(entries: IntersectionObserverEntry[]): void {
  for (const entry of entries) {
    const element = entry.target;
    if (isImpressed(entry)) {
      if (impressed.has(element)) continue;
      if (pendingImpression.has(element)) continue;
      const timer = window.setTimeout(() => {
        pendingImpression.delete(element);
        if (impressed.has(element)) return;
        if (!(element instanceof HTMLElement)) return;
        impressed.add(element);
        // One impression, two sinks. The Sentry span needs a `data-gp-name`
        // payload; the conversion beacon needs only the conversion name and
        // gates on the li_fat_id itself.
        if (element.hasAttribute('data-gp-observe-impression')) {
          const payload = buildPayload(element);
          if (payload) trackInteraction(SPAN_OP.impression, payload);
        }
        const conversionName = element.getAttribute(CONVERSION_IMPRESSION_ATTR);
        if (conversionName) reportImpressionConversion(conversionName);
      }, IMPRESSION_DWELL_MS);
      pendingImpression.set(element, timer);
    } else {
      const timer = pendingImpression.get(element);
      if (timer != null) {
        clearTimeout(timer);
        pendingImpression.delete(element);
      }
    }
  }
}

// Whether an entry currently satisfies either visibility rule (see the
// IMPRESSION_* constants). A section taller than the viewport can't reach 50%
// of its own area on screen, so the viewport-coverage rule is what lets it
// count once it fills half the screen.
function isImpressed(entry: IntersectionObserverEntry): boolean {
  if (entry.intersectionRatio >= IMPRESSION_ELEMENT_FRACTION) return true;
  const root = entry.rootBounds;
  if (!root || root.height === 0) return false;
  // Observed sections are full-bleed, so vertical coverage is what "fills the
  // screen" means here; measuring height keeps it robust if a scrollbar narrows
  // the root's width.
  return entry.intersectionRect.height / root.height >= IMPRESSION_VIEWPORT_FRACTION;
}

// Names an unnamed anchor from where it points (`#history` → `history`), not
// from its text, which is translated. `mailto:`/`tel:` return undefined.
function linkName(link: HTMLAnchorElement): string | undefined {
  let url: URL;
  try {
    url = new URL(link.href, window.location.href);
  } catch {
    return undefined;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
  if (url.hash && url.pathname === window.location.pathname) {
    return url.hash.slice(1) || undefined;
  }
  return pathSegmentName(url.pathname);
}

// The dotted path for an element: its own `data-gp-name` (or `fallbackName`
// when it has none) preceded by every named ancestor, outermost first.
function buildPayload(element: HTMLElement, fallbackName?: string): Attributes | null {
  const target = element.getAttribute('data-gp-name') ?? fallbackName;
  if (!target) return null;

  const names: string[] = [target];
  for (let cursor = element.parentElement; cursor; cursor = cursor.parentElement) {
    const name = cursor.getAttribute('data-gp-name');
    if (name) names.unshift(name);
  }

  return { path: names.join('.'), target };
}
